/**
 * The Claude cloud account rows: which account is connected, and switching it.
 *
 * @module
 */
import { useState } from "react";

import { CmmdClaudeCredentialState } from "../../environments/cmmdApi";
import { CloudProviderSignInController } from "../../hooks/useCloudProviderSignIn";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { toastManager } from "../ui/toast";
import { CloudProviderSignInView } from "./CloudProviderSignInSection";
import {
  ProviderAccountListItem,
  ProviderAccountManagePanel,
  ProviderAccountSelector,
} from "./ProviderAccountManagePanel";

const SCOPE_LABEL = "Stored in your CMMD account. Available to your cloud runtimes.";
const DEFAULT_SCOPE_LABEL = "Default for Claude agent";

// moved verbatim from the 5377-line SettingsPanels.tsx; complexity is
// unchanged, and fallow reads a relocated function as a new one
// fallow-ignore-next-line complexity
export function ClaudeCloudAccountSection({
  cloudSignIn,
  cloudConnected,
  cloudManageable,
  hasKnownAccount,
  cloudAccountEmail,
  cloudPlanLabel,
  claudeAccountSummary,
  claudeAccountRows,
  isClaudeAccountsDialogOpen,
  setIsClaudeAccountsDialogOpen,
  isClaudeCloudSwitching,
  setIsClaudeCloudSwitching,
  selectingClaudeDefaultAccountId,
  onSelectClaudeDefaultAccount,
  onRemoveClaudeAccount,
  removingClaudeAccountId,
}: {
  cloudSignIn: CloudProviderSignInController<CmmdClaudeCredentialState>;
  cloudConnected: boolean;
  cloudManageable: boolean;
  hasKnownAccount: boolean;
  cloudAccountEmail: string | null;
  cloudPlanLabel: string | null;
  claudeAccountSummary: string;
  claudeAccountRows: ReadonlyArray<ProviderAccountListItem>;
  isClaudeAccountsDialogOpen: boolean;
  setIsClaudeAccountsDialogOpen: (open: boolean) => void;
  isClaudeCloudSwitching: boolean;
  setIsClaudeCloudSwitching: (switching: boolean) => void;
  selectingClaudeDefaultAccountId: string | null;
  onSelectClaudeDefaultAccount: (account: ProviderAccountListItem) => void;
  onRemoveClaudeAccount: (accountId: string) => void;
  removingClaudeAccountId: string | null;
}) {
  // Which account a running sign-in belongs to, so the dialog can say so. A
  // reconnect that names no account leaves the user guessing which of their
  // logins the browser is about to ask for.
  const [reconnectingAccount, setReconnectingAccount] = useState<ProviderAccountListItem | null>(
    null,
  );

  const disconnectClaude = () => {
    void cloudSignIn.disconnect().catch((error: unknown) => {
      toastManager.add({
        type: "error",
        title: "Could not disconnect Claude",
        description: error instanceof Error ? error.message : "Try again from Settings.",
      });
    });
  };

  const addAccount = () => {
    setReconnectingAccount(null);
    setIsClaudeCloudSwitching(true);
    setIsClaudeAccountsDialogOpen(true);
    cloudSignIn.start("claudeAgent", { credentialTarget: "linked" });
  };

  const reconnectAccount = (account: ProviderAccountListItem) => {
    setReconnectingAccount(account);
    setIsClaudeCloudSwitching(true);
    // The dialog owns the device code, so a reconnect started from the
    // accordion has to open it or the flow runs where nothing renders it.
    setIsClaudeAccountsDialogOpen(true);
    cloudSignIn.start("claudeAgent", {
      // A linked row reconnects the linked credential. Sending it to the
      // primary would overwrite a different account's login.
      credentialTarget: account.credentialTarget,
    });
  };

  const accountManager = (
    <ProviderAccountManagePanel
      accountName="Claude"
      accounts={claudeAccountRows}
      accountEmail={cloudAccountEmail}
      planLabel={cloudPlanLabel}
      scopeLabel={SCOPE_LABEL}
      isDefaultForNewChats
      defaultScopeLabel={DEFAULT_SCOPE_LABEL}
      needsReconnect={false}
      isSwitching={cloudSignIn.busy}
      isDisconnecting={cloudSignIn.disconnecting}
      canReconnectLinkedAccounts
      onAddAccount={addAccount}
      onReconnectAccount={reconnectAccount}
      onDisconnect={disconnectClaude}
      onRemoveAccount={onRemoveClaudeAccount}
      removingAccountId={removingClaudeAccountId}
    />
  );

  return (
    <div className="min-w-0">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium text-foreground">Subscription</div>
        </div>
        {cloudManageable ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 shrink-0 px-2 text-xs text-muted-foreground hover:text-foreground"
            aria-label="Manage Claude accounts"
            onClick={() => setIsClaudeAccountsDialogOpen(true)}
          >
            Manage
          </Button>
        ) : null}
      </div>
      <ClaudeSubscriptionBody
        cloudSignIn={cloudSignIn}
        cloudConnected={cloudConnected}
        hasKnownAccount={hasKnownAccount}
        claudeAccountSummary={claudeAccountSummary}
        claudeAccountRows={claudeAccountRows}
        selectingClaudeDefaultAccountId={selectingClaudeDefaultAccountId}
        onSelectClaudeDefaultAccount={onSelectClaudeDefaultAccount}
        accountManager={accountManager}
      />
      <Dialog
        open={isClaudeAccountsDialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setIsClaudeAccountsDialogOpen(true);
            return;
          }
          if (isClaudeCloudSwitching) {
            cloudSignIn.cancel();
          }
          setReconnectingAccount(null);
          setIsClaudeCloudSwitching(false);
          setIsClaudeAccountsDialogOpen(false);
        }}
      >
        <DialogPopup className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isClaudeCloudSwitching ? signInDialogTitle(reconnectingAccount) : "Claude accounts"}
            </DialogTitle>
            <DialogDescription>
              {isClaudeCloudSwitching
                ? "Forge uses Claude authorization because the Claude agent runs inside your Forge project."
                : "Claude uses the default account for Claude agent runs."}
            </DialogDescription>
          </DialogHeader>
          <DialogPanel>
            {isClaudeCloudSwitching ? (
              <CloudProviderSignInView
                controller={cloudSignIn}
                provider="claudeAgent"
                connectedMode="manage"
                onCancel={() => {
                  setReconnectingAccount(null);
                  setIsClaudeCloudSwitching(false);
                }}
              />
            ) : (
              accountManager
            )}
          </DialogPanel>
        </DialogPopup>
      </Dialog>
    </div>
  );
}

/**
 * Which of the three subscription states this user is actually in.
 *
 * The middle branch is the one FOR-36206 added. Before it, "no usable account"
 * fell straight through to the connect card, so a user with two accounts that
 * both needed reconnect saw their list replaced by one button naming neither.
 */
function ClaudeSubscriptionBody({
  cloudSignIn,
  cloudConnected,
  hasKnownAccount,
  claudeAccountSummary,
  claudeAccountRows,
  selectingClaudeDefaultAccountId,
  onSelectClaudeDefaultAccount,
  accountManager,
}: {
  cloudSignIn: CloudProviderSignInController<CmmdClaudeCredentialState>;
  cloudConnected: boolean;
  hasKnownAccount: boolean;
  claudeAccountSummary: string;
  claudeAccountRows: ReadonlyArray<ProviderAccountListItem>;
  selectingClaudeDefaultAccountId: string | null;
  onSelectClaudeDefaultAccount: (account: ProviderAccountListItem) => void;
  accountManager: React.ReactNode;
}) {
  if (cloudConnected) {
    return claudeAccountRows.length > 1 ? (
      <ProviderAccountSelector
        accounts={claudeAccountRows}
        defaultScopeLabel={DEFAULT_SCOPE_LABEL}
        disabled={selectingClaudeDefaultAccountId !== null}
        onSelectDefault={onSelectClaudeDefaultAccount}
      />
    ) : (
      <ConnectedClaudeSummary summary={claudeAccountSummary} />
    );
  }
  // Gated on a KNOWN account rather than on the row list being non-empty:
  // claudeAccountRows carries a placeholder row for a never-connected user.
  if (hasKnownAccount) return <>{accountManager}</>;
  return <CloudProviderSignInView controller={cloudSignIn} provider="claudeAgent" />;
}

function ConnectedClaudeSummary({ summary }: { readonly summary: string }) {
  return (
    <div className="rounded-md border border-border/70 bg-background/40 p-3">
      <div className="flex min-w-0 items-start gap-3">
        <span
          aria-hidden="true"
          className="mt-0.5 flex size-3.5 shrink-0 items-center justify-center rounded-full border border-success"
        >
          <span className="size-1.5 rounded-full bg-success" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-xs font-medium text-foreground">Personal Claude</div>
            <span className="rounded-sm border border-success-border bg-success-surface px-1.5 py-0.5 text-[10px] font-medium text-success-foreground">
              {DEFAULT_SCOPE_LABEL}
            </span>
          </div>
          <div className="mt-1 text-xs leading-5 text-muted-foreground">{summary}</div>
        </div>
      </div>
    </div>
  );
}

function signInDialogTitle(account: ProviderAccountListItem | null): string {
  const identity = account?.accountDisplayName ?? account?.accountEmail ?? null;
  return identity === null ? "Sign in with Claude" : `Reconnect ${identity}`;
}
